import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Popover, Slider, Typography } from '@mui/material';
import { MdEdit } from 'react-icons/md';
import { Colors } from '../../constants/theme';
import { formatDateTime } from '../timeAgo';
import { updateVehicleProcessStatus } from '../../api/vehicles';

const IconEdit = MdEdit as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const MARKS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => ({ value }));

type ProgressSliderProps = {
  workSessionId?: string;
  progress?: number;
  updatedAt?: string;
};

const toPercent = (status?: number) => Math.round((status ?? 0) * 100);

export const VehicleProgressSlider = ({
  workSessionId,
  progress,
  updatedAt,
}: ProgressSliderProps) => {
  const [percent, setPercent] = useState(toPercent(progress));
  const [lastUpdated, setLastUpdated] = useState<string | null>(updatedAt ?? null);
  const [isSet, setIsSet] = useState(typeof progress === 'number');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const editButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setPercent(toPercent(progress));
    setLastUpdated(updatedAt ?? null);
    setIsSet(typeof progress === 'number');
  }, [workSessionId, progress, updatedAt]);

  const updateProgressStatus = async (nextPercent: number) => {
    if (!workSessionId) {
      return;
    }
    setIsLoading(true);
    try {
      const nextUpdated = new Date().toISOString();
      await updateVehicleProcessStatus(workSessionId, nextPercent / 100, nextUpdated);
      setLastUpdated(nextUpdated);
      setIsSet(true);
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const lastUpdatedLabel = lastUpdated ? formatDateTime(lastUpdated) : null;
  const chipValue = isSet ? `${percent}%` : '—';

  return (
    <>
      <Box sx={styles.row}>
        <Typography component="span" sx={styles.label}>
          Process status
        </Typography>
        <Typography component="span" sx={styles.value}>
          {chipValue}
        </Typography>
        <Button
          ref={editButtonRef}
          type="button"
          size="small"
          variant="outlined"
          onClick={() => setIsOpen(true)}
          aria-expanded={isOpen}
          aria-label={`Edit process status ${chipValue}`}
          startIcon={<IconEdit style={{ fontSize: 16 }} />}
          sx={styles.editButton}
        >
          Edit
        </Button>
      </Box>

      <Popover
        open={isOpen}
        anchorEl={editButtonRef.current}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: styles.popover } }}
      >
        <Typography component="p" sx={styles.popoverTitle}>
          Process status
        </Typography>
        <Box sx={styles.sliderRow}>
          <Typography component="span" sx={styles.percent}>
            {`${percent}%`}
          </Typography>
          <Slider
            min={0}
            max={100}
            step={10}
            marks={MARKS}
            value={percent}
            onChange={(_event, value) => {
              setPercent(value as number);
              setIsSet(true);
            }}
            onChangeCommitted={(_event, value) => updateProgressStatus(value as number)}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}%`}
            aria-label="Process status"
            sx={styles.slider}
          />
        </Box>
        <Box sx={styles.footer}>
          <Typography component="p" sx={styles.lastUpdated}>
            Last updated: {lastUpdatedLabel ?? '—'}
          </Typography>
          {isLoading && (
            <Box sx={styles.saving}>
              <Typography component="span" sx={styles.savingLabel}>
                Saving...
              </Typography>
              <CircularProgress size={14} />
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

const styles = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  label: {
    fontFamily: 'Futura',
    fontSize: 14,
    fontWeight: 400,
    color: Colors.light.placeholder,
  },
  value: {
    fontFamily: 'Futura',
    fontSize: 22,
    fontWeight: 700,
    color: Colors.light.text,
    lineHeight: 1,
  },
  editButton: {
    textTransform: 'none',
    fontFamily: 'Futura',
    fontSize: 14,
    fontWeight: 500,
    color: Colors.light.green,
    backgroundColor: Colors.light.lightGreen,
    borderColor: 'rgba(0, 145, 73, 0.35)',
    borderRadius: '999px',
    padding: '2px 12px 2px 10px',
    minWidth: 0,
    '& .MuiButton-startIcon': {
      marginRight: '6px',
      marginLeft: 0,
      color: Colors.light.green,
    },
    '&:hover': {
      backgroundColor: Colors.light.lightGreen,
      borderColor: Colors.light.green,
    },
  },
  popover: {
    width: 280,
    padding: '12px 16px 10px',
    marginTop: '8px',
    boxShadow: Colors.shadowStyle.boxShadow,
    borderRadius: '6px',
  },
  popoverTitle: {
    fontFamily: 'Futura',
    fontSize: 13,
    fontWeight: 600,
    color: Colors.light.text,
    margin: 0,
    marginBottom: 0.5,
  },
  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
  percent: {
    fontFamily: 'Futura',
    fontSize: 14,
    fontWeight: 600,
    color: Colors.light.text,
    minWidth: 40,
    flexShrink: 0,
  },
  slider: {
    color: Colors.light.green,
    py: 0.5,
    '& .MuiSlider-markLabel': {
      display: 'none',
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    marginTop: 0.5,
  },
  lastUpdated: {
    fontFamily: 'Futura',
    fontSize: 11,
    fontWeight: 400,
    color: Colors.light.placeholder,
    margin: 0,
  },
  saving: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    flexShrink: 0,
  },
  savingLabel: {
    fontFamily: 'Futura',
    fontSize: 12,
    fontWeight: 400,
    color: Colors.light.placeholder,
  },
};
